<template>
  <div class="flex flex-col overflow-y-hidden">
    <InternalRightSidebar :visible="showFilters" @close="toggleShowFilters">
      <div>
        <SectionHeader>
          <template #left>
            <h1 class="text-md text-base-content">Filters</h1>
          </template>
          <template #right>
            <button
              class="cursor-pointer text-md p-1 -m-1 text-base-content/20 hover:text-primary"
              @click="toggleShowFilters"
            >
              <v-icon name="hi-arrow-sm-right" height="1.5em" width="1.5em" />
            </button>
          </template>
        </SectionHeader>
        <CampaignFilters class="mt-4" />
      </div>
    </InternalRightSidebar>
    <SectionHeader container-class="flex-0">
      <template #left>
        <h1 class="text-md text-base-content">Campaigns</h1>
      </template>
      <template #right>
        <button
          v-if="settings.campaignsViewType == 'grid'"
          v-tooltip="`Switch to List View`"
          @click="toggleView"
          class="p-2 rounded-full cursor-pointer text-base-content/20 shadow-none  hover:text-accent hover:scale-110 transition transform duration-500 ease-in-out"
        >
          <v-icon name="md-formatlistbulleted-round" scale="1.5" class="cursor-pointer" />
        </button>
        <button
          v-if="settings.campaignsViewType == 'list'"
          v-tooltip="`Switch to Grid View`"
          @click="toggleView"
          class="p-2 rounded-full cursor-pointer text-base-content/20 hover:text-accent hover:scale-110 shadow-none transition transform duration-500 ease-in-out"
        >
          <v-icon name="hi-view-grid" scale="1.45" class="cursor-pointer" />
        </button>

        <button
          v-tooltip="showFilters ? `Hide Filters` : 'Show Filters'"
          @click="toggleShowFilters"
          :class="[
            showFilters ? 'text-accent' : 'text-base-content/20',
            'p-2 rounded-full cursor-pointer hover:text-accent hover:scale-110 shadow-none transition transform duration-500 ease-in-out relative',
          ]"
        >
          <v-icon name="md-filteralt-outlined" scale="1.45" class="cursor-pointer" />
          <span
            v-if="activeFiltersCount > 0"
            class="absolute top-1 right-1 text-base-100 bg-accent outline-base-100 outline text-xs rounded-full h-4 w-4 flex items-center justify-center font-medium text-[10px]"
          >
            {{ activeFiltersCount }}
          </span>
        </button>
      </template>
    </SectionHeader>
    <div
      class="flex justify-between"
      :class="[filterSummary.length > 0 ? '' : 'visible sm:hidden']"
    >
      <div class="flex items-center gap-2">
        <span class="text-xxs text-base-content/40 uppercase">Showing:</span>
        <div class="flex items-center flex-wrap gap-1">
          <Tag
            v-if="filterSummary.length > 0"
            v-for="filter in filterSummary"
            :key="filter.label"
            v-tooltip="`${filter.field.charAt(0).toUpperCase() + filter.field.slice(1)}`"
            >{{ filter.label }}
          </Tag>
          <Tag
            v-else="filterSummary.length > 0"
            v-tooltip="showFilters ? `Hide Filters` : 'Show Filters'"
            @click="toggleShowFilters"
            class="cursor-pointer"
            >All Campaigns</Tag>
        </div>
      </div>
      <div class="text-xxs text-base-content/40 uppercase">
        {{ filterSummary.length }} Filters Applied
      </div>
    </div>
    <div class="mt-2">
      <CampaignsGridView
      v-if="settings.campaignsViewType == 'grid'"
      :campaigns="pubCampaigns"
      :isLoading="campaignsLoading"
      @loadMore="loadMore"
    />
    <CampaignsTableView
      v-else
      class="flex-1 overflow-y-scroll"
      :campaigns="pubCampaigns"
      :isLoading="campaignsLoading"
    />

    <div
      v-if="
        campaigns?.publisherCampaigns?.paginatorInfo?.count == 0 &&
        !campaignsLoading
      "
      class="text-sm text-center py-6 text-base-content/80"
    >
      No items found for your search criteria
    </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from "vue"
import { useRoute } from "vue-router"
import { useAuthStore } from "@/stores/auth.js"
import { useCampaignsStore } from "@/stores/campaigns.js"
import { useSettingsStore } from "@/stores/settings.js"
import { useQuery, useMutation } from "@vue/apollo-composable"
import CampaignFilters from "@/pages/campaigns/CampaignFilters.vue"
import InternalRightSidebar from "@/components/common/InternalRightSidebar.vue"
// import QuarantineCampaignAlert from '@/views/Publisher/Campaigns/QuarantineCampaignAlert.vue'
// import PublisherCampaignFilters from '@/views/Publisher/Campaigns/PublisherCampaignFilters.vue'
// import CampaignsListView from '@/views/Publisher/Campaigns/CampaignsListView.vue'
import CampaignsGridView from "@/pages/campaigns/CampaignsGridView.vue"
import CampaignsTableView from "@/pages/campaigns/CampaignsTableView.vue"
import PUBLISHER_CAMPAIGNS_LITE_QUERY from "@/graphql/PublisherCampaign/PublisherCampaignsQueryLite.gql"
import PUBLISHER_CAMPAIGNS_QUERY from "@/graphql/PublisherCampaign/PublisherCampaignsQuery.gql"

import ME_QUERY from "@/graphql/User/MeQuery.gql"
import UPDATE_USER_MUTATION from "@/graphql/User/UpdateUserMutation.gql"

const { settings, setSetting, toggleSetting } = useSettingsStore()

const route = useRoute()
const authStore = useAuthStore()
const campaignsStore = useCampaignsStore()

const loadMoreIncrement = 20
const filters = computed(() => campaignsStore.filters)
const tablePagination = computed(() => campaignsStore.tablePagination)
const gridPagination = computed(() => campaignsStore.gridPagination)

const pagination = computed(() => {
  if (settings.campaignsViewType == "grid") {
    return gridPagination.value
  }
  return tablePagination.value
})

const filterSummary = ref([])

const getFilterSummary = () => {
  const summary = Object.entries(filters.value)
    .filter(filter => {
      const value = filter[1].value

      if (!value) {
        return false
      }

      // check if value is array
      if (Array.isArray(value)) {
        return value && value.length > 0
      }

      if (filter[1].value) return
      filter[1].value && filter[1]?.value.length > 0
    })
    .reduce((acc, filter) => {
      filter[1].value.forEach(filterValue => {
        const label =
          filterValue[filter[1].shortLabelKey] ??
          filterValue[filter[1].optionLabelKey] ??
          filterValue
        const field = filter[1].singularTitle ?? filter[1].title ?? filter[0]
        acc.push({
          field,
          label,
        })
      })

      // acc.push({
      //   field: filter[0],
      //   data: filter[1].value,
      // })
      return acc
    }, [])
  return summary
}

const me = ref(null)

const showFilters = computed(() => {
  return settings.campaignsFiltersVisible
})

const toggleShowFilters = () => {
  toggleSetting("campaignsFiltersVisible")
}

// Count individual filter values for the badge
const activeFiltersCount = computed(() => {
  let count = 0
  
  // Count each filter type
  Object.entries(filters.value).forEach(([key, filter]) => {
    const value = filter.value
    
    if (!value) return
    
    // For array values, count the length
    if (Array.isArray(value)) {
      count += value.length
    } 
    // For string values, count as 1 if not empty
    else if (typeof value === 'string' && value.trim() !== '') {
      count += 1
    }
    // For object values (like offerType), count as 1 if present
    else if (typeof value === 'object' && value !== null) {
      count += 1
    }
  })
  
  return count
})

onMounted(() => {
  const publisherStatus =
    route.query.publisherStatus || route.state?.publisherStatus
  if (publisherStatus) {
    campaignsStore.filters.status.value = [publisherStatus]
  }
})

const campaignFilters = computed(()=>{
  return {
    label: filters.value.campaign.value ? {
      value: [filters.value.campaign.value],
      modifiers: filters.value.campaign.modifiers,
    } : undefined,
    verticals: filters.value.verticals.value ? {
      value: filters.value.verticals.value
        ? filters.value.verticals.value.map(vertical => vertical.name)
        : null,
      modifiers: filters.value.verticals.modifiers,
    } : undefined,
    authUserCountryCode: filters.value.countries.value && filters.value.countries.value.length > 0
      ? {
          value: filters.value.countries.value.map(country => country.code),
        }
      : undefined,
    allowed: filters.value.allowed.value ?{
      value: filters.value.allowed.value.map(value => value.name)
    } : undefined,
    campaignBrowser: filters.value.campaignBrowser.value ? {
      value: filters.value.campaignBrowser.value.map(item => item.name)
    } : undefined,
    campaignOS: filters.value.campaignOS.value ? {
      value: filters.value.campaignOS.value.map(item => item.name)
    } : undefined,
    payoutType: filters.value.payoutType.value ? {
      value: filters.value.payoutType.value.map(item => item)
    }: undefined,
    isSmartLink:
      filters.value.offerType.value === "smart link"
        ? true
        : filters.value.offerType.value === "single offer"
        ? false
        : undefined,
    publisherStatus: filters.value.status.value
      ? { value: filters.value.status.value }
      : undefined,
  }
})


const {
  result: campaigns,
  loading: campaignsLoading,
  onResult,
} = useQuery(PUBLISHER_CAMPAIGNS_LITE_QUERY, () => ({
  first: pagination.value.first,
  page: pagination.value.page,
  filters: campaignFilters.value,
  orderBy: [
    { column: "featured_at", order: "DESC" },
    { column: "id", order: "DESC" },
  ],
}))

const {
  result: fullCampaigns,
  loading: fullCampaignsLoading,
  onResult: onFullResult,
} = useQuery(PUBLISHER_CAMPAIGNS_QUERY, () => ({
  first: pagination.value.first,
  page: pagination.value.page,
  filters: campaignFilters.value,
  orderBy: [
    { column: "featured_at", order: "DESC" },
    { column: "id", order: "DESC" },
  ],
}))


const pubCampaigns = computed(() => {
 

  if(!campaigns.value || !campaigns.value.publisherCampaigns) {
    return {
      data: []
    }
  }

  if(fullCampaigns.value) {
    return fullCampaigns.value.publisherCampaigns
  }

  return campaigns.value.publisherCampaigns
  
})



onResult(({ data, error }) => {
  if (data) {
    const summary = getFilterSummary()
    console.log(summary)
    filterSummary.value = summary
  }
})

onFullResult(({ data, error }) => {
  if (data) {
    filterSummary.value = getFilterSummary()
  }
})


const { result: meResult } = useQuery(ME_QUERY)
me.value = meResult.value?.data?.me

function loadMore() {
  perPage.value += loadMoreIncrement
}

function toggleView() {
  setSetting(
    "campaignsViewType",
    settings.campaignsViewType == "list" ? "grid" : "list",
  )
}

// function gridViewTransform() {
//   toggleView()
//   useMutation({
//     mutation: UPDATE_USER_MUTATION,
//     variables: {
//       input: {
//         id: me.value.id,
//         userDetails: {
//           update: {
//             id: me.value.userDetails.id,
//             gridViewed: true,
//           },
//         },
//       },
//     },
//   })
// }
</script>

<style scoped>
.tooltip {
  display: inline-block;
  position: relative;
  text-align: left;
}

.tooltip .highlight-tooltip {
  @apply bg-amber-300 shadow-md text-black font-semibold text-xs rounded-lg mr-4 py-2 px-4;
  min-width: 205px;
  top: 50%;
  right: 100%;
  transform: translate(0, -50%);
  position: absolute;
  z-index: 99999999;
  box-sizing: border-box;
}

.tooltip .highlight-tooltip i {
  position: absolute;
  top: 50%;
  left: 100%;
  margin-top: -12px;
  width: 12px;
  height: 24px;
  overflow: hidden;
}

.tooltip .highlight-tooltip i::after {
  @apply bg-amber-300 shadow-md;
  content: "";
  position: absolute;
  width: 12px;
  height: 12px;
  left: 0;
  top: 50%;
  transform: translate(-50%, -50%) rotate(-45deg);
}
</style>
